import type { MunicipalityStreetCache } from "./types";

const DATABASE_NAME = "fispro-market-locations";
const DATABASE_VERSION = 1;
const STREET_STORE = "municipality-streets";

const openDatabase = (): Promise<IDBDatabase | null> => {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);

  return new Promise((resolve) => {
    try {
      const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(STREET_STORE)) {
          database.createObjectStore(STREET_STORE, { keyPath: "municipalityId" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
};

export const getCachedMunicipalityStreets = async (
  municipalityId: string
): Promise<MunicipalityStreetCache | undefined> => {
  const database = await openDatabase();
  if (!database) return undefined;

  try {
    return await new Promise((resolve) => {
      const transaction = database.transaction(STREET_STORE, "readonly");
      const request = transaction.objectStore(STREET_STORE).get(municipalityId);
      request.onsuccess = () => resolve(request.result as MunicipalityStreetCache | undefined);
      request.onerror = () => resolve(undefined);
      transaction.oncomplete = () => database.close();
      transaction.onerror = () => database.close();
    });
  } catch {
    database.close();
    return undefined;
  }
};

export const cacheMunicipalityStreets = async (entry: MunicipalityStreetCache): Promise<void> => {
  const database = await openDatabase();
  if (!database) return;

  try {
    await new Promise<void>((resolve) => {
      const transaction = database.transaction(STREET_STORE, "readwrite");
      transaction.objectStore(STREET_STORE).put(entry);
      transaction.oncomplete = () => {
        database.close();
        resolve();
      };
      transaction.onerror = () => {
        database.close();
        resolve();
      };
      transaction.onabort = () => {
        database.close();
        resolve();
      };
    });
  } catch {
    database.close();
  }
};
